import React, { useMemo } from 'react'
import { Typography } from 'antd'
import { CalculationOutputUnit } from './types/municipalityData'
import { createTimeString } from './utils/createTimeString'

export interface ResultData {
  totalExDouble?: number
  hoursPerWeekPerTimepoint: Record<string, number>
  hoursPerWeekTimepointTasks: number
  hoursPerMonthTimepointTasks: number
  hoursPerDayTimepointTasks: number
  hoursPerMonthExtraTasks: number
  hoursPerMonthTimePointsTasksDouble: number
  totalHoursPerMonth: number
  hoursPerMonthSpecificExtraTasks: Record<string, number>
  hoursPerMonthSpecificExtraTasksDouble: Record<string, number>
  timepointTasks: string[]
  extraTasks: string[]
}

interface Props {
  result: ResultData | null
  unit: CalculationOutputUnit
  type?: 'secondary'
  ibic?: boolean
  compact?: boolean
}

interface Texts {
  totalExDouble: string | null
  totalDouble: string | null
  total: string | null
  personalCare: string
  otherCare: string | null
  individual: string | null
  double: string | null
  doubleSuffix: string | null
}

const ShowResults: React.FC<Props> = ({ result, unit, type, ibic, compact }) => {
  // Note: if null text is returned, the field will not be displayed
  const texts = useMemo(() => {
    const tenant_id = localStorage.getItem('tenant_id')

    switch (tenant_id) {
      case process.env.REACT_APP_CLIENT_ID_HULTSFRED:
        return {
          totalExDouble: 'Ramtid',
          totalDouble: null,
          total: null,
          personalCare: 'Tid för insatser vid olika tidpunkter under dagen',
          otherCare: 'Tid för övriga insatser',
          individual: null,
          double: null,
          footer: 'I ramtiden ingår inte tid för egenvård och nattinsatser.',
        }

      case process.env.REACT_APP_CLIENT_ID_NORRKOPING:
        return {
          totalExDouble: 'Ramtid',
          totalDouble: null,
          total: null,
          personalCare: 'Tid för insatser vid olika tidpunkter under dagen',
          otherCare: 'Tid för övriga insatser',
          individual: null,
          double: null,
          footer: 'I ramtiden ingår inte tid för egenvård, ledsagning och nattinsatser.',
        }

      default:
        return {
          totalExDouble: 'Total tid exklusive dubbelbemanning',
          totalDouble: 'Total tid dubbelbemanning',
          total: 'Total tid per månad (Inklusive dubbelbemanning)',
          personalCare: 'Tid för personlig vård per månad',
          otherCare: 'Tid för övriga insatser per månad',
          individual: 'Tid per månad individuellt beräknade övriga insatser',
          double: (value: string | number) => ` (varav: ${value} dubbelbemanning)`,
          footer: null,
        }
    }
  }, [])

  if (result) {
    const totalExDouble = result.totalExDouble ?? result.totalHoursPerMonth - result.hoursPerMonthTimePointsTasksDouble
    const fields = [
      {
        label: texts.totalExDouble,
        value: totalExDouble,
        show: true,
      },
      {
        label: texts.totalDouble,
        value: result.totalHoursPerMonth - totalExDouble,
        show: true,
      },
      {
        label: texts.total,
        value: result.totalHoursPerMonth,
        show: true,
      },
      {
        label: texts.personalCare,
        value: result.hoursPerMonthTimepointTasks,
        show: !ibic,
      },
      {
        label: texts.otherCare,
        value: result.hoursPerMonthExtraTasks,
        show: !ibic,
      },
    ]

    const displayedFields = fields.filter((field) => field.show && field.label)

    return (
      <div className={compact ? 'grid grid-cols-1 gap-4' : 'grid grid-cols-2'}>
        <div>
          <Typography.Title level={3} className="mb-2 text-left">
            Sammanställd tid
          </Typography.Title>
          {displayedFields.map((field) =>
            type === 'secondary' ? (
              <Typography.Paragraph type="secondary" key={field.label}>{`${field.label}: ${createTimeString(field.value, unit)}`}</Typography.Paragraph>
            ) : (
              <Typography key={field.label}>{`${field.label}: ${createTimeString(field.value, unit)}`}</Typography>
            )
          )}
          {texts.footer && (
            <>
              <br />
              <Typography>{texts.footer}</Typography>
            </>
          )}
        </div>
        {result.hoursPerMonthSpecificExtraTasks && Object.keys(result.hoursPerMonthSpecificExtraTasks).length > 0 && (
          <div>
            <Typography className="font-bold">{texts.individual}</Typography>
            {Object.entries(result.hoursPerMonthSpecificExtraTasks).map(([key, value]) => (
              <Typography style={{ fontWeight: 'normal' }} key={key}>
                {`${key}: ${createTimeString(value + (result.hoursPerMonthSpecificExtraTasksDouble[key] ?? 0), unit)}`}

                {/* Potential dubbelbemanning */}
                {result.hoursPerMonthSpecificExtraTasksDouble[key] && texts.double?.(createTimeString(result.hoursPerMonthSpecificExtraTasksDouble[key], unit))}
              </Typography>
            ))}
          </div>
        )}
      </div>
    )
  }
  return null
}

export default ShowResults
